// The analyzing card draws the clip's own waveform, read in the browser while the file uploads.

export const WAVEFORM_BARS = 140;

// Decoded audio takes 4 bytes per sample per channel, so a 10-minute clip at full rate would take
// ~200 MB. Small files decode at 22.05 kHz, which keeps hi-hats and cymbals in the picture; bigger
// ones at 8 kHz (~40 MB for 10 minutes), where the shape still reads.
const LARGE_FILE_BYTES = 30 * 1024 * 1024;

/**
 * Each channel's loudest sample in `bars` equal slices, scaled so the clip's loudest is 1. Takes the
 * left and right sample arrays (the same one twice for mono) and returns { left, right }.
 */
export const peaksOf = ([left, right], bars = WAVEFORM_BARS) => {
  const slice = left.length / bars;
  const peaks = (samples) => Array.from({ length: bars }, (_, bar) => {
    let peak = 0;
    for (let i = Math.floor(bar * slice); i < Math.floor((bar + 1) * slice); i++) {
      const value = Math.abs(samples[i]);
      if (value > peak) peak = value;
    }
    return peak;
  });
  const leftPeaks = peaks(left);
  const rightPeaks = right === left ? leftPeaks : peaks(right);
  const loudest = Math.max(...leftPeaks, ...rightPeaks) || 1;
  return { left: leftPeaks.map((v) => v / loudest), right: rightPeaks.map((v) => v / loudest) };
};

/** The file's waveform as peaksOf gives it, or null when the browser can't decode it */
export const readWaveform = async (file) => {
  try {
    const OfflineContext = window.OfflineAudioContext || window.webkitOfflineAudioContext;
    if (!OfflineContext) return null;
    const context = new OfflineContext(1, 1, file.size > LARGE_FILE_BYTES ? 8000 : 22050);
    const audio = await context.decodeAudioData(await file.arrayBuffer());
    if (!audio.length) return null;
    const left = audio.getChannelData(0);
    return peaksOf([left, audio.numberOfChannels > 1 ? audio.getChannelData(1) : left]);
  } catch {
    return null;
  }
};
